import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Pencil, ShieldCheck, IdCard, BookUser, Briefcase, FileText, CalendarClock, UserCog, Activity as ActIcon, Send, RefreshCw, Ban, Power, Copy, Trash2, Sparkles, Undo2, Check, Plane } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { PageLoading, fmtDate, fmtDateTime, EmptyState } from "@/components/shared";
import { ComplianceBadge, PortalBadge } from "@/components/ComplianceBadge";
import { CompletenessRing, RequirementsChecklist } from "@/components/WorkerRequirements";
import { sectionsFor } from "@/lib/workerTypes";
import { DocumentUpload, DocumentList } from "@/pages/employer/Documents";
import { RenewalGuidance } from "@/components/RenewalGuidance";
import api, { formatApiErrorDetail } from "@/lib/api";

function Row({ label, value }) {
  return <div className="flex justify-between border-b border-slate-50 py-2.5 text-sm"><span className="text-slate-400">{label}</span><span className="font-medium text-navy">{value || "—"}</span></div>;
}
function InfoCard({ title, icon: Icon, children }) {
  return <div className="card-soft p-5"><div className="mb-3 flex items-center gap-2 font-heading font-bold text-navy"><Icon className="h-4 w-4 text-cyan-dark" /> {title}</div>{children}</div>;
}

function PortalAccess({ emp, reload }) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const act = async (fn, label) => {
    setBusy(true);
    try { const { data } = await fn(); if (data?.invite_link) setLink(data.invite_link); toast.success(label); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const s = emp.portal_status;
  return (
    <InfoCard title={t("det.portalAccess")} icon={UserCog}>
      <div className="mb-3"><PortalBadge status={s} /></div>
      {s === "NOT_INVITED" && (
        <>
          <p className="mb-4 text-sm text-slate-500">{t("det.inviteMsg")} <b>{emp.email || t("det.addEmailFirst")}</b>.</p>
          <Button disabled={busy || !emp.email} onClick={() => act(() => api.post(`/employer/employees/${emp.id}/invite?lang=${i18n.language}`), t("det.tInviteSent"))} className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="invite-worker-btn"><Send className="mr-1 h-4 w-4" /> {t("det.inviteWorker")}</Button>
        </>
      )}
      {s === "INVITATION_PENDING" && (
        <>
          <div className="space-y-1 text-sm text-slate-600">
            <Row label={t("det.invitedEmail")} value={emp.invitation?.invited_email} />
            <Row label={t("det.sent")} value={fmtDateTime(emp.invitation?.created_at)} />
            <Row label={t("det.expires")} value={fmtDateTime(emp.invitation?.expires_at)} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" disabled={busy} onClick={() => act(() => api.post(`/employer/employees/${emp.id}/invite/resend?lang=${i18n.language}`), t("det.tInviteResent"))} className="rounded-full bg-navy text-white" data-testid="resend-invite-btn"><RefreshCw className="mr-1 h-3.5 w-3.5" /> {t("det.resend")}</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => api.post(`/employer/employees/${emp.id}/invite/revoke`), t("det.tInviteRevoked"))} className="rounded-full text-red-600" data-testid="revoke-invite-btn"><Ban className="mr-1 h-3.5 w-3.5" /> {t("det.revoke")}</Button>
          </div>
        </>
      )}
      {s === "ACTIVE" && (
        <>
          <div className="space-y-1"><Row label={t("det.portal")} value={t("det.active")} /><Row label={t("det.workerEmail")} value={emp.portal_user?.email} /><Row label={t("det.lastLogin")} value={emp.portal_user?.last_login_at ? fmtDateTime(emp.portal_user.last_login_at) : t("det.never")} /></div>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => api.post(`/employer/employees/${emp.id}/portal/disable`), t("det.tPortalDisabled"))} className="mt-4 rounded-full text-red-600" data-testid="disable-portal-btn"><Ban className="mr-1 h-3.5 w-3.5" /> {t("det.disableAccess")}</Button>
        </>
      )}
      {s === "DISABLED" && (
        <>
          <p className="mb-4 text-sm text-slate-500">{t("det.disabledMsg")}</p>
          <Button size="sm" disabled={busy} onClick={() => act(() => api.post(`/employer/employees/${emp.id}/portal/restore`), t("det.tPortalRestored"))} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="restore-portal-btn"><Power className="mr-1 h-3.5 w-3.5" /> {t("det.restoreAccess")}</Button>
        </>
      )}
      {link && (
        <div className="mt-4 rounded-lg border border-cyan-200 bg-cyan/5 p-3 text-xs">
          <div className="mb-1 font-semibold text-cyan-dark">{t("det.devLink")}</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-white px-2 py-1 text-slate-600">{link}</code>
            <button onClick={() => { navigator.clipboard.writeText(link); toast.success(t("det.tCopied")); }} data-testid="copy-invite-link"><Copy className="h-4 w-4 text-slate-400" /></button>
          </div>
        </div>
      )}
    </InfoCard>
  );
}

export default function EmployeeDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const [emp, setEmp] = useState(null);
  const [acts, setActs] = useState([]);
  const [company, setCompany] = useState(null);
  const [inviteLink, setInviteLink] = useState("");
  const [reqs, setReqs] = useState(null);

  const loadReqs = useCallback(() => api.get(`/employer/employees/${id}/requirements`).then((r) => setReqs(r.data)).catch(() => {}), [id]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/employer/employees/${id}`);
      setEmp(data);
    } catch { toast.error(t("det.tEmployeeNotFound")); nav("/app/employer/employees"); }
  }, [id]);
  useEffect(() => { load(); loadReqs(); api.get("/employer/activity").then((r) => setActs(r.data.activity.filter((a) => a.resource_id === id))); api.get("/employer/company").then((r) => setCompany(r.data.company)); }, [id]);

  const requestDoc = async (docKey) => {
    try { await api.post(`/employer/employees/${id}/request-document`, { doc_key: docKey }); toast.success(t("p2.requestSent")); loadReqs(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!emp) return <PageLoading />;
  const yesNo = (v) => (v ? t("det.yes") : t("det.no"));

  const setEmploymentStatus = async (v) => {
    try { const { data } = await api.post(`/employer/employees/${id}/employment-status`, { employment_status: v }); setEmp(data); toast.success(t("det.statusUpdated")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const inviteWorker = async () => {
    try { const { data } = await api.post(`/employer/employees/${id}/invite?lang=${i18n.language}`); if (data?.invite_link) setInviteLink(data.invite_link); toast.success(t("det.tInviteSent")); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const deleteEmployee = async () => {
    try { await api.delete(`/employer/employees/${id}`); toast.success(t("det.deleted")); nav("/app/employer/employees"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const confirmAutofill = async () => {
    try { const { data } = await api.post(`/employer/employees/${id}/ai-autofill/confirm`); setEmp(data); toast.success(t("det.afConfirmed")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const undoAutofill = async () => {
    try { const { data } = await api.post(`/employer/employees/${id}/ai-autofill/undo`); setEmp(data); toast.success(t("det.afUndone")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const AF_LABELS = { nationality: t("det.afNationality"), permit_valid_until: t("det.afPermitUntil"), passport_valid_until: t("det.afPassportUntil"), passport_number: t("det.afPassportNo"), passport_valid_from: t("det.afPassportFrom"), permit_valid_from: t("det.afPermitFrom") };
  const ES_TONE = { active: "bg-emerald-50 text-emerald-700 border-emerald-200", freistellung: "bg-amber-50 text-amber-700 border-amber-200", terminated: "bg-red-50 text-red-700 border-red-200" };
  const es = emp.employment_status || "active";
  const secs = sectionsFor(emp);
  const tabDefs = [["overview", t("det.tOverview")], ["immigration", t("det.tImmigration")], ["employment", t("det.tEmployment")]];
  if (secs.has("POSTING")) tabDefs.push(["posting", t("p2.sec.POSTING")]);
  if (secs.has("RECOGNITION")) tabDefs.push(["recognition", t("p2.sec.RECOGNITION")]);
  tabDefs.push(["documents", t("det.tDocuments")], ["deadlines", t("det.tDeadlines")], ["portal", t("det.tPortal")], ["activity", t("det.tActivity")]);

  return (
    <div data-testid="employee-detail">
      <Link to="/app/employer/employees" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> {t("det.allEmployees")}</Link>
      <div className="card-soft mb-6 flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-navy to-cyan-dark text-xl font-bold text-white">{emp.first_name[0]}{emp.last_name[0]}</span>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-navy">{emp.first_name} {emp.last_name}</h1>
            {emp.worker_id && <div className="text-xs text-slate-400" data-testid="detail-worker-id">Worker ID: {emp.worker_id}</div>}
            <div className="text-sm text-slate-500">{emp.nationality || "—"} · {emp.job_title || "—"}</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <ComplianceBadge status={emp.compliance_status} />
              <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500">{t("det.workAuthShort")}: {emp.work_authorization_status || "—"}</span>
              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${ES_TONE[es]}`} data-testid="employment-status-badge">{t(`det.es${es.charAt(0).toUpperCase() + es.slice(1)}`)}</span>
              {emp.worker_category && emp.worker_category !== "NOT_CLASSIFIED" && <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600" data-testid="detail-worker-category">{t(`wc.${emp.worker_category}`)}</span>}
              {emp.employment_context && emp.employment_context !== "DIRECT_EMPLOYEE" && <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">{t(`p2.ctx.${emp.employment_context}`)}</span>}
              {emp.worker_location_status === "ABROAD" && <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700" data-testid="detail-abroad-badge">{t("p2.abroadBadge")}</span>}
            </div>
          </div>
        </div>
        {reqs?.completeness && <div className="flex flex-col items-center gap-1"><CompletenessRing pct={reqs.completeness.pct} /><span className="text-[11px] text-slate-400">{t("p2.completeness")}</span></div>}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={es} onValueChange={setEmploymentStatus}>
            <SelectTrigger className="h-9 w-40 rounded-full" data-testid="employment-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t("det.esActive")}</SelectItem>
              <SelectItem value="freistellung">{t("det.esFreistellung")}</SelectItem>
              <SelectItem value="terminated">{t("det.esTerminated")}</SelectItem>
            </SelectContent>
          </Select>
          {emp.portal_status === "NOT_INVITED" && (
            <Button variant="outline" className="rounded-full" disabled={!emp.email} onClick={inviteWorker} data-testid="header-invite-btn"><Send className="mr-1 h-4 w-4" /> {t("det.inviteWorker")}</Button>
          )}
          <Link to={`/app/employer/employees/${id}/edit`}><Button variant="outline" className="rounded-full" data-testid="edit-employee-btn"><Pencil className="mr-1 h-4 w-4" /> {t("det.edit")}</Button></Link>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="rounded-full text-red-600 hover:text-red-700" data-testid="delete-employee-btn"><Trash2 className="mr-1 h-4 w-4" /> {t("det.delete")}</Button></AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-employee-dialog">
              <AlertDialogHeader><AlertDialogTitle>{t("det.deleteTitle")}</AlertDialogTitle><AlertDialogDescription>{t("det.deleteConfirm", { name: `${emp.first_name} ${emp.last_name}` })}</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>{t("det.cancel")}</AlertDialogCancel><AlertDialogAction onClick={deleteEmployee} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-employee">{t("det.delete")}</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {inviteLink && (
        <div className="mb-6 flex flex-col gap-2 rounded-xl border border-cyan-200 bg-cyan/5 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="header-invite-link-box">
          <div className="min-w-0">
            <div className="text-sm font-bold text-navy">{t("det.tInviteSent")}</div>
            <div className="mt-1 truncate text-xs text-slate-500">{inviteLink}</div>
          </div>
          <Button size="sm" variant="outline" className="shrink-0 rounded-full" onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success(t("det.tCopied")); }} data-testid="header-copy-invite-link"><Copy className="mr-1 h-4 w-4" /> {t("det.copy")}</Button>
        </div>
      )}

      {emp.ai_autofill?.status === "pending" && (
        <div className="mb-6 flex flex-col gap-3 rounded-xl border border-cyan-200 bg-cyan/5 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="ai-autofill-banner">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-cyan-dark" />
            <div>
              <div className="text-sm font-bold text-navy">{t("det.afTitle")}</div>
              <p className="mt-0.5 text-xs text-slate-600">{t("det.afDesc")}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(emp.ai_autofill.fields || {}).map(([k, v]) => (
                  <span key={k} className="rounded-full border border-cyan-200 bg-white px-2 py-0.5 text-[11px] text-slate-600" data-testid={`af-field-${k}`}><b>{AF_LABELS[k] || k}</b>: {v.new || "—"}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={undoAutofill} data-testid="af-undo-btn"><Undo2 className="mr-1 h-4 w-4" /> {t("det.afUndo")}</Button>
            <Button size="sm" className="rounded-full bg-cyan text-white hover:bg-cyan-dark" onClick={confirmAutofill} data-testid="af-confirm-btn"><Check className="mr-1 h-4 w-4" /> {t("det.afConfirm")}</Button>
          </div>
        </div>
      )}

      {["ACTION_REQUIRED", "CRITICAL"].includes(emp.compliance_status) && (
        <div className="mb-6"><RenewalGuidance emp={emp} company={company} /></div>
      )}

      {(emp.worker_category === "NON_EU" || emp.worker_location_status === "ABROAD") && (
        <div className="card-soft mb-6 flex flex-wrap items-center justify-between gap-3 p-5" data-testid="emp-immigration-entry">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan/10 text-cyan-dark"><Plane className="h-5 w-5" /></span>
            <div>
              <div className="font-heading font-bold text-navy">{t("im.sectionTitle")}</div>
              <p className="text-xs text-slate-500">{emp.worker_location_status === "ABROAD" ? t("im.abroadHint") : t("im.inGermanyHint")}</p>
            </div>
          </div>
          <Button onClick={async () => { try { const { data } = await api.post("/employer/immigration/cases", { worker_id: id }); nav(`/app/employer/immigration/${data.id}`); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }} className="rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="emp-open-immigration">{t("im.startOrOpen")}</Button>
        </div>
      )}

      <div className="card-soft mb-6 flex flex-wrap items-center justify-between gap-3 p-5" data-testid="emp-contract-entry">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan/10 text-cyan-dark"><FileText className="h-5 w-5" /></span>
          <div><div className="font-heading font-bold text-navy">Arbeitsvertrag</div><p className="text-xs text-slate-500">Arbeitsvertrag mit EmployGuard AI erstellen.</p></div>
        </div>
        <Button onClick={async () => { try { const { data } = await api.post("/employer/contracts", { worker_id: id }); nav(`/app/employer/contracts/${data.id}`); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }} className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="emp-create-contract">Arbeitsvertrag erstellen</Button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
          {tabDefs.map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-full border border-slate-200 px-4 py-1.5 data-[state=active]:bg-navy data-[state=active]:text-white">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            <InfoCard title={t("det.workAuthorization")} icon={ShieldCheck}><Row label={t("det.status")} value={emp.work_authorization_status} /><Row label={t("det.restriction")} value={yesNo(emp.employer_restriction)} /></InfoCard>
            <InfoCard title={t("det.residencePermit")} icon={IdCard}><Row label={t("det.status")} value={emp.residence_status} /><Row label={t("det.legalBasis")} value={emp.legal_basis} /><Row label={t("det.validUntil")} value={fmtDate(emp.permit_valid_until)} /></InfoCard>
            <InfoCard title={t("det.passport")} icon={BookUser}><Row label={t("det.number")} value={emp.passport_number} /><Row label={t("det.country")} value={emp.passport_country} /><Row label={t("det.validUntil")} value={fmtDate(emp.passport_valid_until)} /></InfoCard>
            <InfoCard title={t("det.employment")} icon={Briefcase}><Row label={t("det.jobTitle")} value={emp.job_title} /><Row label={t("det.department")} value={emp.department} /><Row label={t("det.type")} value={emp.employment_type} /></InfoCard>
            <InfoCard title={t("det.nextAction")} icon={CalendarClock}><p className="text-sm font-medium text-cyan-dark">{emp.next_action}</p>{emp.permit_days_remaining != null && <p className="mt-2 text-xs text-slate-400">{t("det.permitDays", { n: emp.permit_days_remaining })}</p>}</InfoCard>
            <InfoCard title={t("det.complianceSummary")} icon={ShieldCheck}><ComplianceBadge status={emp.compliance_status} /><p className="mt-3 text-xs text-slate-500">{t("det.statusComputed")}</p></InfoCard>
          </div>
        </TabsContent>

        <TabsContent value="immigration">
          {emp.worker_location_status === "ABROAD" && (
            <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="immigration-abroad-note">
              <span className="font-semibold">{t("p2.sec.IMMIGRATION")}:</span> {t("p2.immigrationNotStarted")}
            </div>
          )}
          <div className="grid gap-5 md:grid-cols-2">
            <InfoCard title={t("det.residenceAndPermit")} icon={IdCard}>
              <Row label={t("det.residenceStatus")} value={emp.residence_status} /><Row label={t("det.permitType")} value={emp.permit_type} /><Row label={t("det.legalBasis")} value={emp.legal_basis} />
              <Row label={t("det.validFrom")} value={fmtDate(emp.permit_valid_from)} /><Row label={t("det.validUntil")} value={fmtDate(emp.permit_valid_until)} /><Row label={t("det.workAuthorization2")} value={emp.work_authorization_status} /><Row label={t("det.employerRestriction")} value={yesNo(emp.employer_restriction)} /><Row label={t("det.arbeitgeberwechsel")} value={yesNo(emp.arbeitgeberwechsel)} />
            </InfoCard>
            <InfoCard title={t("det.passport")} icon={BookUser}>
              <Row label={t("det.number")} value={emp.passport_number} /><Row label={t("det.issuingCountry")} value={emp.passport_country} /><Row label={t("det.validFrom")} value={fmtDate(emp.passport_valid_from)} /><Row label={t("det.validUntil")} value={fmtDate(emp.passport_valid_until)} />
            </InfoCard>
            {emp.notes && <div className="md:col-span-2"><InfoCard title={t("det.notes")} icon={FileText}><p className="text-sm text-slate-600">{emp.notes}</p></InfoCard></div>}
          </div>
        </TabsContent>

        <TabsContent value="employment">
          <InfoCard title={t("det.employmentDetails")} icon={Briefcase}>
            <Row label={t("det.jobTitle")} value={emp.job_title} /><Row label={t("p2.positionTitle")} value={emp.position_title} /><Row label={t("p2.profession")} value={emp.profession} /><Row label={t("det.department")} value={emp.department} /><Row label={t("det.type")} value={emp.employment_type} />
            <Row label={t("det.startDate")} value={fmtDate(emp.employment_start_date)} /><Row label={t("det.endDate")} value={fmtDate(emp.employment_end_date)} /><Row label={t("det.employeeNumber")} value={emp.employee_number} />
          </InfoCard>
        </TabsContent>

        {secs.has("POSTING") && (
          <TabsContent value="posting">
            <InfoCard title={t("p2.sec.POSTING")} icon={Briefcase}>
              <Row label={t("p2.foreignEmployer")} value={emp.foreign_employer} /><Row label={t("p2.homeCountry")} value={emp.home_country} />
              <Row label={t("p2.postingStart")} value={fmtDate(emp.posting_start)} /><Row label={t("p2.postingEnd")} value={fmtDate(emp.posting_end)} />
              <Row label={t("p2.a1From")} value={fmtDate(emp.a1_valid_from)} /><Row label={t("p2.a1Until")} value={fmtDate(emp.a1_valid_until)} />
              <Row label={t("p2.sendingInstitution")} value={emp.sending_institution} /><Row label={t("p2.germanAssignment")} value={emp.german_assignment} />
            </InfoCard>
          </TabsContent>
        )}

        {secs.has("RECOGNITION") && (
          <TabsContent value="recognition">
            <InfoCard title={t("p2.sec.RECOGNITION")} icon={ShieldCheck}>
              <Row label={t("p2.recognitionStatus")} value={emp.recognition_status ? t(`p2.rec.${emp.recognition_status}`) : "—"} />
              <Row label={t("p2.recognitionAuthority")} value={emp.recognition_authority} /><Row label={t("p2.recognitionDate")} value={fmtDate(emp.recognition_date)} />
            </InfoCard>
          </TabsContent>
        )}


        <TabsContent value="documents">
          {reqs && <div className="card-soft mb-5 p-5"><div className="mb-3 font-heading font-bold text-navy">{t("p2.requiredDocs")}</div><RequirementsChecklist reqs={reqs} onRequest={requestDoc} /></div>}
          <DocumentSection employeeId={id} />
        </TabsContent>

        <TabsContent value="deadlines"><EmployeeDeadlines employeeId={id} /></TabsContent>

        <TabsContent value="portal"><div className="max-w-lg"><PortalAccess emp={emp} reload={load} /></div></TabsContent>

        <TabsContent value="activity">
          <div className="card-soft p-6">
            {acts.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">{t("det.noActivity")}</p> :
              <div className="space-y-3">{acts.map((a) => <div key={a.id} className="flex items-center gap-3 border-b border-slate-50 py-2 text-sm"><ActIcon className="h-4 w-4 text-slate-400" /><span className="font-medium text-navy">{a.action.replace(/[._]/g, " ")}</span><span className="ml-auto text-xs text-slate-400">{fmtDateTime(a.created_at)}</span></div>)}</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DocumentSection({ employeeId }) {
  const [docs, setDocs] = useState(null);
  const load = () => api.get(`/employer/documents?employee_id=${employeeId}`).then((r) => setDocs(r.data.documents));
  useEffect(() => { load(); }, [employeeId]);
  return (
    <div>
      <div className="mb-4 flex justify-end"><DocumentUpload employeeId={employeeId} onDone={load} /></div>
      {docs === null ? <PageLoading /> : <DocumentList docs={docs} onChange={load} showEmployee={false} />}
    </div>
  );
}

function EmployeeDeadlines({ employeeId }) {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/employer/deadlines").then((r) => setItems(r.data.deadlines.filter((d) => d.employee_id === employeeId))); }, [employeeId]);
  if (items === null) return <PageLoading />;
  if (items.length === 0) return <EmptyState icon={CalendarClock} title={t("det.caughtUp")} subtitle={t("det.noEmpDeadlines")} />;
  return (
    <div className="card-soft divide-y divide-slate-50 p-2">
      {items.map((d) => (
        <div key={d.id} className="flex items-center justify-between px-4 py-3">
          <div><div className="text-sm font-semibold text-navy">{d.type === "RENEWAL" ? t("dlp.renewalTitle") : d.title}</div><div className="text-xs text-slate-400">{d.type === "RENEWAL" ? t("dlp.renewalType") : d.type}</div></div>
          <div className="text-right"><div className="text-sm text-navy">{fmtDate(d.due_date)}</div>{d.days_remaining != null && <div className={`text-xs ${d.days_remaining < 0 ? "text-red-500" : d.days_remaining <= 30 ? "text-amber-500" : "text-slate-400"}`}>{d.days_remaining < 0 ? t("det.overdue") : t("det.daysN", { n: d.days_remaining })}</div>}</div>
        </div>
      ))}
    </div>
  );
}
